import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import "bootstrap/dist/css/bootstrap.min.css";
import { useContext, useEffect, useRef, useState } from "react";
import { Alert, Col } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { ReactComponent as AscendingHoverIcon } from '../../Assets/img/AscendingHoverIcon.svg';
import { ReactComponent as AscendingIcon } from '../../Assets/img/AscendingIcon.svg';
import { ReactComponent as CheckBoxPartial } from '../../Assets/img/CheckBoxPartial.svg';
import { ReactComponent as CheckBoxPartialHover } from '../../Assets/img/CheckBoxPartialHover.svg';
import { ReactComponent as CheckBoxSelected } from '../../Assets/img/CheckBoxSelected.svg';
import { ReactComponent as CheckBoxSelectedHover } from '../../Assets/img/CheckBoxSelectedHover.svg';
import { ReactComponent as CheckBoxUnselected } from '../../Assets/img/CheckBoxUnselected.svg';
import { ReactComponent as CheckBoxUnselectedHover } from '../../Assets/img/CheckBoxUnselectedHover.svg';
import { ReactComponent as DescendingHoverIcon } from '../../Assets/img/DescendingHoverIcon.svg';
import { ReactComponent as DescendingIcon } from '../../Assets/img/DescendingIcon.svg';
import { ReactComponent as Edit } from '../../Assets/img/EditPen.svg';
import { ReactComponent as EditPenHover } from '../../Assets/img/EditPenHover.svg';
import { ReactComponent as Hide } from '../../Assets/img/Hide.svg';
import { ReactComponent as Show } from '../../Assets/img/Show.svg';
import AuthenticationService from "../../Services/AuthenticationService";
import PartnerService from "../../Services/PartnerService";
import AuthContext from '../AuthContextComponent/AuthContext';
import LoadingIndicator from "../CommonComponent/LoadingIndicator";
import NoMatch from "../CommonComponent/NoMatch";
import Footer from "../FooterComponent/Footer";
import Navigation from "../NavigationComponent/Navigation";
import TopNavigation from "../NavigationComponent/TopNavigation";
import PageTable from "../PageComponent/PageTable";
import PageTop from "../PageComponent/PageTop";
import EditPartner from "../PartnerComponent/EditPartner";
import MessagePopup from "../PopupComponent/MessagePopup";
import TextFilterBox from "../PopupComponent/TextFilterBox";
import WarningMessagePopup from "../PopupComponent/WarningMessagePopup";
import AddPartner from "./AddPartner";


const columnHelper = createColumnHelper();
const columns = [columnHelper.accessor('Partner Name', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Partner Name</span>,
    id: "PartnerName",
    meta: {
        sortFieldName: "PartnerName",
        type: "string"
    }
}), columnHelper.accessor('Contact Number', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Contact Number</span>,
    id: "ContactNumber",
    meta: {
        sortFieldName: "ContactNumber",
        type: "string"
    }
}), columnHelper.accessor('Email Id', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Email Id</span>,
    id: "EmailId",
    meta: {
        sortFieldName: "EmailId",
        type: "string"
    }
}), columnHelper.accessor('Organization Name', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Organization Name</span>,
    id: "OrganizationName",
    meta: {
        sortFieldName: "OrganizationName",
        type: "string"
    }
}), columnHelper.accessor('Common Name', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Common Name</span>,
    id: "CommonName",
    meta: {
        sortFieldName: "CommonName",
        type: "string"
    }
})];

/**
 * Renders partner page with all the partner and partner functions
 * @component
 */
function Partner() {

    //#region States

    const [state, setState] = useState({
        status: 'LOADING',
        partnerList: null,
        popup: false,
        message: null,
        error: null,
        sortField: null,
        sortType: null,
        selectedItems: [],
        showFilter: false,
        allColumnFilter: null,
        searchPartnerName: "",
        partnerNameFilter: null,
        searchContactNumber: "",
        contactNumberFilter: null,
        searchEmail: "",
        emailFilter: null,
        searchOrganizationName: "",
        organizationNameFilter: null,
        searchCommonName: "",
        commonNameFilter: null,
        isTableLoading: false,
        model: null,
        perPage: 20,
        currentPage: 0,
        pageCount: 0,
        totalDataCount: 0,
        selectedItemsForPopup: [],
        hiddenColumns: [],
        allPartners: [],
        triggerCSVDownload: false,
        countryId: null,
        partnerId: null
    });

    //#endregion

    //#region Refs

    const searchValue = useRef();
    const csvRef = useRef();

    const [data, setData] = useState([]);

    const table = useReactTable({
        data,
        columns,
        columnResizeMode: "onChange",
        getCoreRowModel: getCoreRowModel(),
    });

    const authCtx = useContext(AuthContext);
    //#endregion

    //#region CSV headers

    const csvHeader = [
        { label: "Partner Name", key: "partnerName" },
        { label: "Contact Number", key: "contactNumber" },
        { label: "Email Id", key: "emailId" },
        { label: "Organization Name", key: "organizationName" },
        { label: "Common Name", key: "commonName" }
    ];

    //#endregion

    //#region useEffect hook

    /**
    * Calls loadPartnerList() method to get partner details
    * Runs on state change of state.currentPage, state.sortField, state.sortType, state.perPage, state.allColumnFilter, state.partnerNameFilter,state.contactNumberFilter, state.emailFilter, state.organizationNameFilter,state.commonNameFilter
    */
    useEffect(() => {
        async function fetchData() {
            await loadPartnerList();
        }
        fetchData();
    }, [state.sortField, state.sortType, state.allColumnFilter, state.perPage, state.currentPage, state.partnerNameFilter, state.contactNumberFilter, state.emailFilter,
    state.organizationNameFilter, state.commonNameFilter]);

    /**
    * Calls loadPartnerList() method to get partner details
    * Runs only on state change of state.success
    */
    useEffect(() => {
        async function fetchData() {
            await loadPartnerList();
        }
        if (state.success == true) {
            fetchData();
        }
    }, [state.success]);

    /**
    * Triggers csv download for exporting partner details
    * Runs only on state change of state.triggerCSVDownload
    */
    useEffect(() => {
        if (state.triggerCSVDownload) {
            csvRef.current.link.click();
            setState((currentState) => ({
                ...currentState,
                triggerCSVDownload: false
            }));
        }
    }, [state.triggerCSVDownload])

    //#endregion

    //#region Methods

    /**
    * Calls partner service to fetch partners according to filters
    */
    const loadPartnerList = async () => {
        setState((currentState) => ({
            ...currentState,
            isTableLoading: true,
        }));

        try {
            var request = {
                Filters: [state.allColumnFilter, state.partnerNameFilter, state.contactNumberFilter, state.emailFilter,
                state.organizationNameFilter, state.commonNameFilter].filter(f => f != null),
                Sort: (state.sortField == null || state.sortType == null) ? [] : [{
                    SortField: state.sortField,
                    sortType: state.sortType
                }],
                Pagination: {
                    PageStart: state.currentPage,
                    PageFetch: state.perPage
                }
            }

            const response = await PartnerService.GetPartnersPerPage(request);

            var responseD = [];
            response.data.forEach((partner) => {
                responseD.push({
                    "Partner Name": partner.partnerName,
                    "Contact Number": partner.contactNumber,
                    "Email Id": partner.emailId,
                    "Organization Name": partner.organizationName,
                    "Common Name": partner.commonName
                })
            })
            setData(responseD);

            console.log(response);

            setState((currentState) => ({
                ...currentState,
                isTableLoading: false,
                status: 'LOADED',
                partnerList: response.data,
                pageCount: Math.ceil(
                    response.totalRecord / state.perPage
                ),
                totalDataCount: response.totalRecord
            }));
        } catch (error) {
            setState((currentState) => ({
                ...currentState,
                status: 'ERROR',
                error: error
            }));
        }
    }

    /**
    * Sort the given field  ("asc" to "desc", "desc" to "asc", null to "asc") 
    * @param   {String} field  Coloumn name that user wants to change 
    */
    const sort = (field) => {
        var type = state.sortType;
        var fieldValue = state.sortField;
        if (type == null) {
            type = "asc";
        }
        else if (type == "asc") {
            if (fieldValue == field) {
                type = "desc";
            }
            else {
                type = "asc";
            }
        }
        else {
            if (fieldValue == field) {
                type = null;
            }
            else {
                type = "asc";
            }
        }

        setState((currentState) => ({
            ...currentState,
            sortField: field,
            sortType: type,
            selectedItems: []
        }));
    }

    /**
    * Push all the partners into the list of selected partners 
    */
    const selectAllItems = () => {
        var selected = [];
        state.partnerList.forEach(partner => {
            selected.push(partner);
        });
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
    * Remove all the partners from the list of selected partners 
    */
    const unSelectAllItems = () => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: []
        }));
    }

    /**
   * Push the partner into the list of selected partners 
   * @param   {Object} partner  Object that contains partner details
   */
    const selectItem = (partner) => {
        var selected = state.selectedItems;
        selected.push(partner);
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
    * Remove the partner from the list of selected partners 
    * @param   {Object} partner  Object that contains partner details
    */
    const unSelectItem = (partner) => {
        var selected = state.selectedItems.filter(sel => sel != partner);
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
    * Search partners
    */
    const searchAllColumn = () => {
        if (searchValue.current.value == "") {
            setState((currentState) => ({
                ...currentState,
                allColumnFilter: null
            }));
        }
        else {
            setState((currentState) => ({
                ...currentState,
                allColumnFilter: {
                    Type: "contains",
                    Value: searchValue.current.value,
                    ColumnNames: ["partnername", "contactnumber", "emailid", "organizationname", "commonname"]
                }
            }));
        }
    }

    /**
    * Search partners by partner name
    * @param    {Event} event  User action (on changing value)
    */
    const searchByPartnerName = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchPartnerName: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["partnername"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            partnerNameFilter: filter
        }));
    }

    /**
    * Search partners by contact number
    * @param    {Event} event  User action (on changing value)
    */
    const searchByContactNumber = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchContactNumber: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["contactnumber"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            contactNumberFilter: filter
        }));
    }

    /**
    * Search partners by email id
    * @param    {Event} event  User action (on changing value)
    */
    const searchByEmailId = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchEmail: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["emailid"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            emailFilter: filter
        }));
    }

    /**
    * Search partners by organization name
    * @param    {Event} event  User action (on changing value)
    */
    const searchByOrganizationName = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchOrganizationName: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["organizationname"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            organizationNameFilter: filter
        }));
    }

    /**
    * Search partners by common name
    * @param    {Event} event  User action (on changing value)
    */
    const searchByCommonName = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchCommonName: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["commonname"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            commonNameFilter: filter
        }));
    }

    /**
    * Move to next page
    */
    const nextPageHandler = () => {
        if (state.currentPage + 2 > state.pageCount) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.currentPage + 1,
            selectedItems: []
        }));
    }

    /**
    * Move to previous page
    */
    const previousPageHandler = () => {
        if (state.currentPage - 1 < 0) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.currentPage - 1,
            selectedItems: []
        }));
    }

    /**
    * Move to first page
    */
    const firstPageHandler = () => {
        if (state.currentPage == 0) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: 0,
            selectedItems: []
        }));
    }

    /**
    * Move to last page
    */
    const lastPageHandler = () => {
        if (state.currentPage == state.pageCount - 1) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.pageCount - 1,
            selectedItems: []
        }))
    }

    /**
    * Changes the number of partners to display per page according to user input
    * @param    {Event} event  User action (on changing value)
    */
    const changePerPage = (event) => {
        setState((currentState) => ({
            ...currentState,
            perPage: event.target.value,
            currentPage: 0,
            selectedItems: []
        }));
    }

    /**
    * Clear all the applied filters
    */
    const clearFilters = () => {
        setState((currentState) => ({
            ...currentState,
            partnerNameFilter: null,
            contactNumberFilter: null,
            emailFilter: null,
            organizationNameFilter: null,
            commonNameFilter: null,
            searchPartnerName: "",
            searchContactNumber: "",
            searchEmail: "",
            searchOrganizationName: "",
            searchCommonName: ""
        }))
    }

    /**
    * Show the selected column in partner table
    * @param    {String} columnName  Column name that user selected to show
    */
    const showColumn = (columnName) => {
        if (columnName == "") {
            setState((currentState) => ({
                ...currentState,
                hiddenColumns: []
            }));
        }

        var columns = state.hiddenColumns.filter(c => c != columnName);

        setState((currentState) => ({
            ...currentState,
            hiddenColumns: columns
        }));
    }

    /** Hide the selected column from partner table
    * @param    { String } columnName  Column name that user selected to hide
    */
    const hideColumn = (columnName) => {
        var columns = state.hiddenColumns;
        columns.push(columnName);

        setState((currentState) => ({
            ...currentState,
            hiddenColumns: columns
        }));
    }

    /**
    * Show all the columns in partner table
    */
    const showAllColumns = () => {
        setState((currentState) => ({
            ...currentState,
            hiddenColumns: []
        }));
    }

    /**
    * Calls partner service to fetch all partners
    */
    const getAllPartners = async () => {
        PartnerService.getPartners().then((partners) => {
            setState((currentState) => ({
                ...currentState,
                allPartners: partners,
                triggerCSVDownload: true
            }));
        }).catch((error) => {
            setState((currentState) => ({
                ...currentState,
                status: "ERROR",
                error: error,
            }));
        });
    }

    /**
   * Opens model
   * @param    {String} model  Name of model to open
   * @param    {String} param  Value of parameter corresponding to model
   */
    const modelOpenHandler = (model, param1, param2) => {
        if (model == "activate" || model == "deactivate") {
            setState((currentState) => ({
                ...currentState,
                configKey: param1
            }));
        }
        else if (model == "edit") {
            setState((currentState) => ({
                ...currentState,
                countryId: param1,
                partnerId: param2
            }));
        }

        setState((currentState) => ({
            ...currentState,
            model: model
        }));
    }

    /**
   * Display message
   * @param    {String} message  Message needs to be displayed
   */
    const messageDisplayHandler = async (message) => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: [],
            selectedItemsForPopup: [],
            success: true,
            message: message
        }));
    }

    /**
    * Deletes all the selected partners
    */
    const DeletePartners = async () => {
        try {
            var tenantIds = [];
            state.selectedItems.forEach(partner => tenantIds.push({
                CountryId: partner.countryId,
                PartnerId: partner.partnerId
            }));
            var request = {
                Ids: tenantIds
            }

            await PartnerService.BulkPartnerDelete(request);

            AuthenticationService.GetTenants()
                .then((responseT) => {
                    authCtx.setTenants(JSON.stringify(responseT.response));
                });

            setState((currentState) => ({
                ...currentState,
                selectedItems: [],
                model: null,
                success: true,
                message: "Partners deleted",
            }));
        } catch (error) {
            setState((currentState) => ({
                ...currentState,
                status: "ERROR",
                error: error
            }));
        }
    };

    /**
   * Closes message popup
   */
    const messagePopupCloseHandler = () => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: [],
            selectedItemsForPopup: [],
            success: null,
            message: null
        }));
    }

    /**
   * Closes opened model
   */
    const modelCloseHandler = async () => {
        setState((currentState) => ({
            ...currentState,
            model: null
        }));
    }

    /**
    * Reset the page to default
    */
    const resetPage = () => {
        setState((currentState) => ({
            ...currentState,
            status: "LOADED",
            error: null,
            perPage: 20,
            currentPage: 0,
            sortField: null,
            sortType: null,
            selectedItems: [],
            showFilter: false,
            allColumnFilter: null,
            partnerNameFilter: null,
            contactNumberFilter: null,
            emailFilter: null,
            organizationNameFilter: null,
            commonNameFilter: null,
            searchPartnerName: "",
            searchContactNumber: "",
            searchEmail: "",
            searchOrganizationName: "",
            searchCommonName: "",
            isTableLoading: false,
            model: null,
            success: null,
            message: null,
            hiddenColumns: [],
            allPartners: [],
            selectedItemsForPopup: []
        }));
        searchValue.current.value = ""
        table.getHeaderGroups().map((headerGroup) => (
            headerGroup.headers.map((header) => (
                header.column.resetSize()
            ))
        ))
    }

    //#endregion

    return (
        <>
            {state.status == "LOADING" && <LoadingIndicator size="large" message="Fetching..." />}
            {state.status == "ERROR" && (state.error?.message?.indexOf("404") > -1 ? <NoMatch /> : <Alert variant="danger">{state.error?.message}</Alert>)}
            {state.status == "LOADED" &&
                <>
                    <Navigation />
                    <TopNavigation onPageReset={resetPage} />
                    <div className="page-container">
                        {state.model == "add" && <AddPartner onShowMessage={messageDisplayHandler} onClose={modelCloseHandler} action="Yes" />}
                        {state.model == "edit" && <EditPartner onShowMessage={messageDisplayHandler} onClose={modelCloseHandler} action="Yes" countryId={state.countryId} partnerId={state.partnerId} />}
                        {state.success && <MessagePopup onClose={messagePopupCloseHandler} title="Success" body={<div className="message-popup-container-body"> {state.message}
                            {state.selectedItemsForPopup.length != 0 &&
                                <table className="popup-table">
                                    <tr>
                                        <th className="warning-message-popup-first">ConfigKey</th>
                                        <th className="warning-message-popup-last">ConfigValue</th>
                                    </tr>
                                    <tbody className="popup-tbody"> {state.selectedItemsForPopup?.slice(0, 3).map(partner => (
                                        <tr>
                                            <td className="warning-message-popup-first">
                                                {partner.ConfigKey}
                                            </td>
                                            <td className="warning-message-popup-last">
                                                {partner.ConfigValue}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                    <div className="bottom-title">
                                        {state.selectedItemsForPopup?.length > 3 && <p>... and {state.selectedItemsForPopup.length - 3} more {state.selectedItemsForPopup.length - 3 == 1 ? 'record' : 'records'}. </p>}
                                    </div>
                                </table>}
                        </div>} />}
                        {state.error != null && <MessagePopup title="Failure" body={state.error} />}
                        {state.model == "delete" && <WarningMessagePopup onClose={modelCloseHandler} title="Delete Partners" body={<div className="message-popup-container-body"> Do you want to Delete :
                            {state.selectedItems != null &&
                                <table className="popup-table">
                                    <tr>
                                        <th className="warning-message-popup-first">Partner Name</th>
                                        <th className="warning-message-popup-last">Email Id</th>
                                    </tr>
                                    <tbody className="popup-tbody"> {state.selectedItems?.slice(0, 3).map(partner => (
                                        <tr>
                                            <td className="warning-message-popup-first">
                                                {partner.partnerName}
                                            </td>
                                            <td className="warning-message-popup-last">
                                                {partner.emailId}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                    <div className="bottom-title">
                                        {state.selectedItems?.length > 3 && <p>... and {state.selectedItems.length - 3} more {state.selectedItems.length - 3 == 1 ? 'record' : 'records'} ? </p>}
                                    </div>
                                </table>}
                        </div>} action="Yes" onAction={DeletePartners} />}
                        <PageTop searchPlaceholder="Search Partners"
                            disableUploadButton
                            disableSecondAddButton
                            addButtonText="Register Partner"
                            searchRef={searchValue}
                            showFilter={state.showFilter}
                            modelOpenHandler={modelOpenHandler}
                            searchAllColumnHandler={searchAllColumn}
                            resetPageHandler={resetPage}
                            filterColumnHandler={() => {
                                setState((currentState) => ({ ...currentState, showFilter: !currentState.showFilter }));
                                if (state.showFilter) {
                                    clearFilters();
                                }
                            }}
                            menuData={<div className="menu-popup-container">
                                <span className="bold-underline-text" onClick={() => {
                                    showAllColumns();
                                }}>
                                    ShowAll
                                </span>
                                <div className="columns-container">
                                    {table.getHeaderGroups().map(headerGroup => (
                                        headerGroup.headers.map(header => (
                                            <div className="column-container">
                                                <span className="column-text">{header.column.columnDef.accessorKey}</span>
                                                <span className="column-icon">
                                                    {state.hiddenColumns?.includes(header.id) ?
                                                        <Hide onClick={() => {
                                                            showColumn(header.id);
                                                        }} />
                                                        : <Show style={{ opacity: 0.5 }} onClick={() => {
                                                            hideColumn(header.id);
                                                        }} />}
                                                </span>
                                            </div>
                                        ))
                                    ))}
                                </div>
                                <div style={{ margin: "0 8px 10px 8px", borderTop: "1px solid #ddd" }}></div>
                                <div className="columns-container">
                                    <div className="bottom-column-container">
                                        {state.partnerList.length === 0 ?
                                            (<span className="bottom-column-text" style={{ opacity: 0.5 }}>
                                                Export All...
                                            </span>) :
                                            (<>
                                                <span className="bottom-column-text" onClick={getAllPartners} style={{ cursor: "pointer", maxWidth: "fit-content" }}>Export All...</span>
                                                <CSVLink
                                                    data={state.allPartners}
                                                    headers={csvHeader}
                                                    separator={","}
                                                    filename={"Partners.csv"}
                                                    asyncOnClick={true}
                                                    ref={csvRef} target="_blank" ></CSVLink>
                                            </>)}
                                    </div>
                                    <div className="bottom-column-container">
                                        {state.selectedItems.length === 0 ?
                                            (<span className="bottom-column-text" style={{ opacity: 0.5 }}>
                                                Export Selected...
                                            </span>) :
                                            (<span>
                                                <CSVLink className="bottom-column-text"
                                                    data={state.selectedItems}
                                                    headers={csvHeader}
                                                    separator={","}
                                                    filename={"Partners.csv"}>
                                                    Export Selected...
                                                </CSVLink>
                                            </span>)}
                                    </div>
                                </div>
                            </div>} />
                        <PageTable isTableLoading={state.isTableLoading}
                            tableData={
                                <table>
                                    <thead className="table-header">
                                        <tr class="normal-header">
                                            <th scope="col" style={{ padding: "0 5px 0 5px" }}>
                                                {state.selectedItems.length == 0 ?
                                                    <Col className="menu-icon-container">
                                                        <CheckBoxUnselected onClick={selectAllItems} />
                                                        <CheckBoxUnselectedHover className="hover-icon" onClick={selectAllItems} />
                                                    </Col>
                                                    :
                                                    (state.selectedItems.length == state.partnerList.length ?
                                                        <Col className="menu-icon-container">
                                                            <CheckBoxSelected onClick={unSelectAllItems} />
                                                            <CheckBoxSelectedHover className="hover-icon" onClick={unSelectAllItems} />
                                                        </Col> :
                                                        <Col className="menu-icon-container">
                                                            <CheckBoxPartial onClick={selectAllItems} />
                                                            <CheckBoxPartialHover className="hover-icon" onClick={selectAllItems} />
                                                        </Col>)}
                                            </th>
                                            {table.getHeaderGroups().map(headerGroup => (
                                                headerGroup.headers.map(header => (
                                                    !state.hiddenColumns.includes(header.id) &&
                                                    <th key={header.id} scope="col"
                                                        style={{ width: header.getSize(), cursor: "pointer", textAlign: header.column.columnDef.meta.type == "date" ? "right" : (header.column.columnDef.meta.type == "bool" ? "center" : "left") }}>
                                                        <div className="header-text-wrapper" onClick={() => { sort(header.column.columnDef.meta.sortFieldName) }}>
                                                            {state.sortField == header.column.columnDef.meta.sortFieldName && state.sortType == "asc" ?
                                                                <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`}>
                                                                    <DescendingIcon />
                                                                    <AscendingHoverIcon />
                                                                </div> : (state.sortField == header.column.columnDef.meta.sortFieldName && state.sortType == "desc" ?
                                                                    <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`} >
                                                                        <DescendingHoverIcon />
                                                                        <AscendingIcon />
                                                                    </div> : <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`} >
                                                                        <DescendingIcon />
                                                                        <AscendingIcon />
                                                                    </div>)}
                                                            {header.isPlaceholder
                                                                ? null
                                                                : flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                        </div>
                                                        <div className={`resize-handle ${header.column.getIsResizing() ? 'active' : null}`} onMouseDown={header.getResizeHandler()} onTouchStart={header.getResizeHandler()}></div>
                                                    </th>
                                                ))
                                            ))}
                                            <th scope="col"></th>
                                        </tr>
                                        {state.showFilter &&
                                            <tr class="filter-header">
                                                <th scope="col"></th>
                                                {!state.hiddenColumns.includes("PartnerName") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchPartnerName: "", partnerNameFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchPartnerName}
                                                            searchHandler={searchByPartnerName} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("ContactNumber") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchContactNumber: "", contactNumberFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchContactNumber}
                                                            searchHandler={searchByContactNumber} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("EmailId") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchEmail: "", emailFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchEmail}
                                                            searchHandler={searchByEmailId} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("OrganizationName") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchOrganizationName: "", organizationNameFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchOrganizationName}
                                                            searchHandler={searchByOrganizationName} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("CommonName") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchCommonName: "", commonNameFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchCommonName}
                                                            searchHandler={searchByCommonName} />
                                                    </th>}
                                                <th scope="col"></th>
                                            </tr >
                                        }
                                    </thead>
                                    {!state.isTableLoading &&
                                        <tbody >
                                            {table.getRowModel().rows.map(row => (
                                                <tr key={row.id} className={state.selectedItems.find(sel => sel.partnerName == state.partnerList[row.id].partnerName) == null ? '' : 'selected'}>
                                                    <td scope="col">
                                                        {state.selectedItems.find(sel => sel.partnerName == state.partnerList[row.id].partnerName) == null ?
                                                            <Col className="menu-icon-container">
                                                                <CheckBoxUnselected onClick={() => { selectItem(state.partnerList[row.id]) }} />
                                                                <CheckBoxUnselectedHover className="hover-icon" onClick={() => { selectItem(state.partnerList[row.id]) }} />
                                                            </Col>
                                                            :
                                                            <Col className="menu-icon-container">
                                                                <CheckBoxSelected onClick={() => { unSelectItem(state.partnerList[row.id]) }} />
                                                                <CheckBoxSelectedHover className="hover-icon" onClick={() => { unSelectItem(state.partnerList[row.id]) }} />
                                                            </Col>
                                                        }
                                                    </td>
                                                    {row.getVisibleCells().map(cell => (
                                                        !state.hiddenColumns.includes(cell.column.id) &&
                                                        (cell.column.columnDef.meta.type == "date" ?
                                                            <td scope="col" key={cell.id} style={{ textAlign: "right" }}>
                                                                <div className="body-text-wrapper">
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </div>
                                                            </td> :
                                                            <td scope="col" key={cell.id}>
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>)
                                                    )
                                                    )}
                                                    <td scope="col">
                                                        <span className="menu-icon-container">
                                                            <Edit className="menu-icon" onClick={() => modelOpenHandler("edit", state.partnerList[row.id].countryId, state.partnerList[row.id].partnerId)} />
                                                            <EditPenHover className="menu-icon hover-icon" onClick={() => modelOpenHandler("edit", state.partnerList[row.id].countryId, state.partnerList[row.id].partnerId)} />
                                                        </span>
                                                    </td>
                                                </tr>

                                            ))}
                                        </tbody>}
                                </table>
                            } />
                        <Footer onFirstClick={firstPageHandler}
                            onLastClick={lastPageHandler}
                            onPreviousClick={previousPageHandler}
                            onNextClick={nextPageHandler}
                            perPage={state.perPage}
                            totalData={state.totalDataCount}
                            showingItemsCount={state.partnerList.length}
                            currentPage={state.currentPage + 1}
                            totalPage={state.pageCount}
                            selectedItems={state.selectedItems.length}
                            changePerPage={changePerPage}
                            modelOpenHandler={modelOpenHandler}
                            disableEditButton
                            disableLine
                            disableDeleteButton
                        />
                    </div>
                </>
            }
        </>
    )
}

export default Partner;
